// Pure helpers for parsing the free-text `group_preference` field on
// Attendee and resolving each parsed name fragment against the attendee
// pool. See docs/superpowers/specs/2026-05-27-group-preference-fulfillment-design.md
// for the rationale behind every choice in here.

import type { Attendee } from './types';

// Splits the free-text preference on common separators. Used by both the
// auto-assign cluster pre-pass and the Roster tooltip renderer.
//
// Separators: `,`, `;`, `&`, and the standalone word `and` (case-
// insensitive, word-bounded so "Andy" / "Andrew" / "Sandra" don't split).
//
// Output is trimmed, empty-fragment-filtered, and case-insensitively
// deduped (preserving the first-seen casing).
export const parsePreference = (text: string): string[] => {
  if (!text) return [];
  const parts = text.split(/\s*(?:,|;|&|\band\b)\s*/i);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of parts) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
};

// Iterative Levenshtein with two rolling rows (O(n) space). Used to
// score last-name fuzziness (parents misspell other kids' last names
// constantly).
//
// Inputs are compared case-sensitively — callers normalize to lowercase
// before passing in (see resolveFragment).
export const levenshtein = (a: string, b: string): number => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let prev = new Array<number>(b.length + 1);
  let curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1, // insertion
        prev[j] + 1, // deletion
        prev[j - 1] + cost, // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
};

// Normalized similarity in [0, 1]: 1 - dist / max(len). Returns 0 when
// both inputs are empty (no signal). Used as a secondary check when
// Levenshtein alone would be too strict for long names.
export const similarityRatio = (a: string, b: string): number => {
  if (!a.length && !b.length) return 0;
  const maxLen = Math.max(a.length, b.length);
  return 1 - levenshtein(a, b) / maxLen;
};

export type ResolutionStatus =
  | 'matched' // exactly one same-grade candidate passes
  | 'cross-grade' // exactly one any-grade candidate passes, grade differs
  | 'ambiguous' // ≥2 same-grade candidates pass with comparable scores
  | 'not-found' // no candidate passes
  | 'incomplete-name'; // fragment is single-word (first name only)

export type Resolution = {
  fragment: string;
  status: ResolutionStatus;
  matchedAttendee?: Attendee;
  ambiguousCandidates?: Attendee[];
};

// Last-name similarity check: pass if Levenshtein ≤ 2 OR ratio ≥ 0.8.
// Both inputs are normalized lowercase before this is called.
const lastNamePasses = (a: string, b: string): boolean =>
  levenshtein(a, b) <= 2 || similarityRatio(a, b) >= 0.8;

// Tokenizes "First Last [Middle/extra]" into (first, last). The first
// whitespace-separated word is the first name; everything else collapses
// into the last name. Single-word inputs return ('', '').
const splitFragment = (fragment: string): { first: string; last: string } => {
  const tokens = fragment.trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return { first: '', last: '' };
  const [first, ...rest] = tokens;
  return { first, last: rest.join(' ') };
};

// Finds candidates in `pool` whose first name matches `first` exactly
// (case-insensitively) AND whose last name passes the fuzzy check.
// Excludes the source attendee.
const findCandidates = (
  pool: Attendee[],
  first: string,
  last: string,
  source: Attendee,
): Attendee[] => {
  const firstLc = first.toLowerCase();
  const lastLc = last.toLowerCase();
  return pool.filter((a) => {
    if (a.ref === source.ref) return false;
    if ((a.first_name ?? '').toLowerCase() !== firstLc) return false;
    return lastNamePasses((a.last_name ?? '').toLowerCase(), lastLc);
  });
};

// Resolves a single name fragment to a single attendee (or a typed
// failure mode) using exact-first-name + fuzzy-last-name matching.
// See the spec's "Resolving a fragment" section for the rationale.
export const resolveFragment = (
  fragment: string,
  source: Attendee,
  allAttendees: Attendee[],
): Resolution => {
  const { first, last } = splitFragment(fragment);
  if (!first || !last) {
    return { fragment, status: 'incomplete-name' };
  }

  const sourceGrade = source.grade_completed;
  const sameGradePool = sourceGrade
    ? allAttendees.filter((a) => a.grade_completed === sourceGrade)
    : [];

  const sameGradeCandidates = findCandidates(
    sameGradePool,
    first,
    last,
    source,
  );

  if (sameGradeCandidates.length === 1) {
    return {
      fragment,
      status: 'matched',
      matchedAttendee: sameGradeCandidates[0],
    };
  }
  if (sameGradeCandidates.length >= 2) {
    return {
      fragment,
      status: 'ambiguous',
      ambiguousCandidates: sameGradeCandidates,
    };
  }

  // Zero same-grade matches — try the whole pool for a cross-grade hit.
  const anyGradeCandidates = findCandidates(allAttendees, first, last, source);
  if (anyGradeCandidates.length === 1) {
    return {
      fragment,
      status: 'cross-grade',
      matchedAttendee: anyGradeCandidates[0],
    };
  }
  return { fragment, status: 'not-found' };
};

export type PreferenceResolution = {
  raw: string;
  fragments: Resolution[];
  matchedRefs: string[];
};

// Top-level resolver: parses the attendee's group_preference text and
// resolves each parsed fragment. `matchedRefs` is the deduplicated list
// of attendee refs successfully matched in the same grade (the only
// fragments that drive auto-assign clustering); the full `fragments`
// list is preserved for tooltip rendering, which surfaces unmatched /
// cross-grade / ambiguous / incomplete fragments to the admin.
export const resolveAttendeePreference = (
  attendee: Attendee,
  allAttendees: Attendee[],
): PreferenceResolution => {
  const raw = attendee.group_preference ?? '';
  const fragmentTexts = parsePreference(raw);
  const fragments = fragmentTexts.map((f) =>
    resolveFragment(f, attendee, allAttendees),
  );
  const matchedRefSet = new Set<string>();
  for (const f of fragments) {
    if (f.status === 'matched' && f.matchedAttendee) {
      matchedRefSet.add(f.matchedAttendee.ref);
    }
  }
  return {
    raw,
    fragments,
    matchedRefs: Array.from(matchedRefSet),
  };
};
